import type { NewsResponses as ApiResponses } from '@/types/externals/obbsApi/index';
import type { Pagination } from '@/types/commons/PaginateType';
import { NEWS_CATEGORY_NAME } from '@/consts/NewsCategoryConst';

export const useNews = () => {
  const news = useState<ApiResponses.News[]>('news', () => []);

  /**
   * 最新30件を取る。件数とページはキャッシュ側（`server/api/public-cache/[resource].get.ts`）の表で固定している。
   * 失敗したらページ全体をエラーにする。ステータスは一時的な障害として 503
   * （Nitro ルートのエラー本文は h3 の形で、API のエラー本文の `status`・`title` は無い）。
   */
  const fetchNews = async (): Promise<void> => {
    const { data, error } = await usePublicCache<Pagination<ApiResponses.News>>('news');

    if (error.value) {
      throw createError({
        statusCode: 503,
        statusMessage: 'Service Unavailable',
        message: `Failed to fetch news: ${error.value.message}`,
        fatal: true,
      });
    }

    if (!data.value) return;

    news.value = data.value.data;
  };

  const getAllNews = (): ApiResponses.News[] => {
    return news.value;
  };

  const getImportantNews = (): ApiResponses.News[] => {
    return news.value.filter(
      (item: ApiResponses.News) => item.newsCategory === NEWS_CATEGORY_NAME.IMPORTANT
    );
  };

  const getNews = (id: number): ApiResponses.News => {
    const currentNews = news.value.find((item: ApiResponses.News) => item.id === id);
    if (!currentNews) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Not Found',
        message: `The target news was not found.`,
        fatal: true,
      });
    }

    return currentNews;
  };

  /**
   * 支給デザインの前後ナビ（`news-nav`）用に、指定した記事の隣を返す。
   *
   * 配列は API が掲載日降順で返したまま（`obbs_api` の `NewsService::getPaginatedNews` が
   * `displayDateDesc()`）なので、一覧の並びと同じ。**画面の並びに合わせて
   * `prev`（左・「前へ」）は1つ新しい記事、`next`（右・「次へ」）は1つ古い記事**とする。
   *
   * 母集団は `plugins/202-fetch-news.ts` が取得した最新30件なので、
   * 30件目の記事に `next` は出ない（31件目が配列に無い）。
   */
  const getAdjacentNews = (
    id: number
  ): { prev: ApiResponses.News | null; next: ApiResponses.News | null } => {
    const index = news.value.findIndex((item: ApiResponses.News) => item.id === id);
    if (index === -1) {
      return { prev: null, next: null };
    }

    return {
      prev: news.value[index - 1] ?? null,
      next: news.value[index + 1] ?? null,
    };
  };

  return {
    fetchNews,
    getAllNews,
    getImportantNews,
    getNews,
    getAdjacentNews,
  };
};
